/**
 * ForgotPasswordPage — Self-service password reset for Cognito users.
 * Two steps: 1) Enter email to receive reset code, 2) Enter code + new password.
 */

import { useState, type FormEvent } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { isNepEmail } from "../authMode";

type Step = "request" | "confirm";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestCode = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (isNepEmail(email)) {
      setError("NEP Group users (@nepgroup.com) use Okta SSO. Password reset is managed through Okta.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword({ username: email.toLowerCase() });
      setStep("confirm");
    } catch (err: any) {
      if (err.name === "UserNotFoundException") {
        setError("No account found with this email address.");
      } else if (err.name === "LimitExceededException") {
        setError("Too many attempts. Please try again later.");
      } else {
        setError(err.message || "Failed to send reset code. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmReset = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    setIsSubmitting(true);
    try {
      await confirmResetPassword({
        username: email.toLowerCase(),
        confirmationCode: code,
        newPassword,
      });
      // Success — redirect to login
      navigate("/login", { state: { message: "Password reset successful. Please sign in with your new password." } });
    } catch (err: any) {
      if (err.name === "CodeMismatchException") {
        setError("Invalid code. Please check your email and try again.");
      } else if (err.name === "ExpiredCodeException") {
        setError("Code expired. Please request a new one.");
      } else {
        setError(err.message || "Failed to reset password. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
      <Card style={{ width: "100%", maxWidth: "440px" }}>
        <Card.Body className="p-4">
          <div className="text-center mb-4">
            <img
              src="/nep-logo.png"
              alt="NEP Group"
              className="mb-3"
              style={{ maxWidth: "160px", height: "auto" }}
            />
            <h2 className="fw-bold" style={{ color: "#0e67f5" }}>AnyPlay</h2>
            <p className="text-muted">
              {step === "request" ? "Reset Password" : "Set New Password"}
            </p>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {step === "request" && (
            <Form onSubmit={handleRequestCode}>
              <p className="text-muted" style={{ fontSize: "0.85rem" }}>
                Enter your email address and we'll send you a verification code to reset your password.
              </p>

              <Form.Group className="mb-3">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                  autoFocus
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100 mb-3" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Sending Code...</>
                ) : (
                  "Send Reset Code"
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Remember your password? <Link to="/login">Sign In</Link>
                </small>
              </div>
            </Form>
          )}

          {step === "confirm" && (
            <Form onSubmit={handleConfirmReset}>
              <Alert variant="info">
                We've sent a verification code to <strong>{email}</strong>. Enter it below with your new password.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Verification Code</Form.Label>
                <Form.Control
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Enter 6-digit code"
                  required
                  maxLength={6}
                  autoFocus
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  required
                  minLength={8}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Confirm New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="w-100 mb-3" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Spinner animation="border" size="sm" className="me-2" /> Resetting...</>
                ) : (
                  "Reset Password"
                )}
              </Button>

              <div className="text-center">
                <small className="text-muted">
                  Didn't receive a code?{" "}
                  <button
                    type="button"
                    className="btn btn-link p-0 text-decoration-none"
                    style={{ fontSize: "inherit" }}
                    onClick={() => { setStep("request"); setError(null); }}
                  >
                    Try again
                  </button>
                </small>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
